import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { NaturezaOperacaoService } from '../../../../../../shared/services/apis/motor-fiscal/natureza-operacao.service';
import { NaturezaOperacaoLookupStruct } from '../../../../../../shared/services/structs/motor-fiscal/natureza-operacao-lookup.struct';
import { NaturezaOperacaoRegraTributariaStruct } from '../../../../../../shared/services/structs/motor-fiscal/natureza-operacao-regra-tributaria.struct';
import { TipoOperacaoEnum } from '../../../../../../shared/enums/motor-fiscal/tipo-operacao.enum';
import { TipoPadraoNaturezaEnum } from '@shared/enums/motor-fiscal/tipo-padrao-natureza.enum';
import { TipoPadraoNaturezaOperacaoStruct } from '@shared/services/structs/motor-fiscal/tipo-padrao-natureza-operacao.struct';

import { LoadingBoxDefaultListComponent } from '../../../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-regra-tributaria-natureza-step',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatTableModule,
        NgxMaskPipe,
        LoadingBoxDefaultListComponent,
        EmptyStateComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './regra-tributaria-natureza-step.component.html',
    styleUrl: './regra-tributaria-natureza-step.component.css',
})
export class RegraTributariaNaturezaStepComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() naturezasSelecionadas: NaturezaOperacaoRegraTributariaStruct[] = [];
    @Output() naturezasSelecionadasChange = new EventEmitter<NaturezaOperacaoRegraTributariaStruct[]>();

    public readonly TipoOperacaoEnum = TipoOperacaoEnum;
    public readonly tipoLabel: Record<number, string> = {
        [TipoOperacaoEnum.Entrada]: 'Entrada',
        [TipoOperacaoEnum.Saida]: 'Saída',
    };
    /** Catálogo dos tipos de padrão, devolvido junto do lookup das naturezas. */
    public tiposPadrao: TipoPadraoNaturezaOperacaoStruct[] = [];
    public readonly cfopMaskPatterns = { '0': { pattern: /[0-9?]/ } };

    public model: FormGroup;
    public displayedColumns = ['select', 'nome', 'cfop', 'tipo', 'padrao'];
    public dataSource = new MatTableDataSource<NaturezaOperacaoLookupStruct>();
    public allData: NaturezaOperacaoLookupStruct[] = [];
    public paginaAtual = 1;
    public tamanhoPagina = 20;
    public totalRegistros = 0;
    public isLoading = false;

    constructor(
        private fb: FormBuilder,
        private naturezaService: NaturezaOperacaoService,
        private alertService: AlertService,
    ) {
        this.model = this.fb.group({ pesquisa: [''] });
    }

    ngOnInit(): void {
        this.carregarNaturezas(true);
    }

    public get naturezaIds(): number[] {
        return this.form.get('naturezas')?.value ?? [];
    }

    public clickPesquisar(): void {
        this.paginaAtual = 1;
        this.allData = [];
        this.dataSource.data = [];
        this.carregarNaturezas(true);
    }

    public carregarNaturezas(reset = false): void {
        this.isLoading = true;
        const pesquisa = this.model.get('pesquisa')?.value || null;

        // Vínculo regra x natureza só existe para operação de SAÍDA -> filtra a listagem.
        // O lookup traz só naturezas ativas, as únicas que podem ser vinculadas.
        this.naturezaService.getLookup(pesquisa, TipoOperacaoEnum.Saida, this.paginaAtual, this.tamanhoPagina).subscribe({
            next: response => {
                this.isLoading = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar naturezas', AlertType.error);
                    return;
                }
                this.totalRegistros = response.totalRegistros;
                this.tiposPadrao = response.tiposPadrao ?? [];
                this.allData = reset ? response.naturezas : [...this.allData, ...response.naturezas];
                this.dataSource.data = this.allData;
            },
            error: err => {
                this.isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar naturezas', AlertType.error);
            },
        });
    }

    /** Nome do tipo de padrão lido do catálogo. */
    public nomeTipoPadrao(tipo: TipoPadraoNaturezaEnum): string {
        return this.tiposPadrao.find(t => t.idTipoPadraoNaturezaOperacao === tipo)?.nome ?? '';
    }

    public proximaPagina(): void {
        this.paginaAtual++;
        this.carregarNaturezas();
    }

    public isSelecionada(row: NaturezaOperacaoLookupStruct): boolean {
        return this.naturezaIds.includes(row.idNaturezaOperacao);
    }

    public isAllVisibleSelected(): boolean {
        return this.dataSource.data.length > 0 && this.dataSource.data.every(row => this.naturezaIds.includes(row.idNaturezaOperacao));
    }

    public toggleAllVisible(): void {
        if (this.isAllVisibleSelected()) {
            const idsVisiveis = this.dataSource.data.map(row => row.idNaturezaOperacao);
            this.atualizarNaturezas(this.naturezaIds.filter(id => !idsVisiveis.includes(id)));
            this.emitirSelecionadas(this.naturezasSelecionadas.filter(n => !idsVisiveis.includes(n.idNaturezaOperacao)));
        } else {
            const novosIds = [...this.naturezaIds];
            const selecionadas = [...this.naturezasSelecionadas];
            for (const row of this.dataSource.data) {
                if (!novosIds.includes(row.idNaturezaOperacao)) {
                    novosIds.push(row.idNaturezaOperacao);
                    selecionadas.push(this.toRegraNatureza(row));
                }
            }
            this.atualizarNaturezas(novosIds);
            this.emitirSelecionadas(selecionadas);
        }
    }

    public toggleRow(row: NaturezaOperacaoLookupStruct): void {
        if (this.isSelecionada(row)) {
            this.atualizarNaturezas(this.naturezaIds.filter(id => id !== row.idNaturezaOperacao));
            this.emitirSelecionadas(this.naturezasSelecionadas.filter(n => n.idNaturezaOperacao !== row.idNaturezaOperacao));
            return;
        }

        this.atualizarNaturezas([...this.naturezaIds, row.idNaturezaOperacao]);
        this.emitirSelecionadas([...this.naturezasSelecionadas, this.toRegraNatureza(row)]);
    }

    private atualizarNaturezas(ids: number[]): void {
        this.form.get('naturezas')?.setValue([...new Set(ids)]);
        this.form.get('naturezas')?.markAsDirty();
    }

    private emitirSelecionadas(naturezas: NaturezaOperacaoRegraTributariaStruct[]): void {
        const mapa = new Map<number, NaturezaOperacaoRegraTributariaStruct>();
        naturezas.forEach(n => mapa.set(n.idNaturezaOperacao, n));
        const list = Array.from(mapa.values());
        this.naturezasSelecionadas = list;
        this.naturezasSelecionadasChange.emit(list);
    }

    private toRegraNatureza(row: NaturezaOperacaoLookupStruct): NaturezaOperacaoRegraTributariaStruct {
        return {
            idNaturezaOperacao: row.idNaturezaOperacao,
            nome: row.nome,
            cfopPadrao: row.cfopPadrao,
            idTipoOperacao: row.idTipoOperacao,
        };
    }
}
